import { useEffect } from 'react';
import { toast } from 'react-toastify';
import useAuthStore from '../store/useAuthStore';
import socketManager from '../api/socket';
import { UserPlus, LogIn, LogOut, X, AlertCircle } from 'lucide-react';

// Custom toast component with theme support
const CustomToast = ({ title, message, icon: Icon, color }) => (
  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
    {Icon && (
      <div style={{
        width: '40px',
        height: '40px',
        borderRadius: 'var(--radius-lg)',
        backgroundColor: `var(--${color}-bg)`,
        color: `var(--${color})`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}>
        <Icon size={20} />
      </div>
    )}
    <div style={{ flex: 1 }}>
      <div style={{ 
        fontWeight: '600', 
        marginBottom: message ? '0.25rem' : 0,
        color: 'var(--text)',
      }}>
        {title}
      </div>
      {message && (
        <div style={{ 
          fontSize: '0.875rem', 
          color: 'var(--text-secondary)',
          lineHeight: 1.4,
        }}>
          {message}
        </div>
      )}
    </div>
  </div>
);

// Keep track of recent notifications to prevent duplicates
const recentNotifications = new Set();

export default function NotificationToastListener() {
  const { user } = useAuthStore();

  useEffect(() => {
    if (!user) return;

    console.log('NotificationToastListener: Setting up listener');
    
    const handleNewNotification = (notification) => {
      // Create a unique key for this notification
      const notificationKey = `${notification._id}-${notification.timestamp || notification.createdAt}`;
      
      // Check if we've already shown this notification
      if (recentNotifications.has(notificationKey)) {
        console.log('NotificationToastListener: Skipping duplicate notification:', notificationKey);
        return;
      }
      
      // Add to recent notifications and remove after 5 seconds
      recentNotifications.add(notificationKey);
      setTimeout(() => {
        recentNotifications.delete(notificationKey);
      }, 5000);
      
      console.log('NotificationToastListener: Showing notification:', notification.title, notification.type);
      
      // Configure toast based on notification type
      let toastConfig = {
        icon: false,
        style: {
          backgroundColor: 'var(--bg-elevated)',
          color: 'var(--text)',
          border: '1px solid var(--border)',
          boxShadow: 'var(--shadow-lg)',
          borderRadius: 'var(--radius-lg)',
        },
        progressStyle: {
          backgroundColor: 'var(--gold)',
        },
      };

      // Show different toast types based on notification type
      switch (notification.type) {
        case 'visit_created':
          toast(
            <CustomToast 
              title={notification.title}
              message={notification.message}
              icon={UserPlus}
              color="primary"
            />,
            toastConfig
          );
          break;
          
        case 'visit_arrived':
          toast(
            <CustomToast 
              title={notification.title}
              message={notification.message}
              icon={LogIn}
              color="success"
            />,
            toastConfig
          );
          break;
          
        case 'visit_exited':
          toast(
            <CustomToast 
              title={notification.title}
              message={notification.message}
              icon={LogOut}
              color="info"
            />,
            toastConfig
          );
          break;
          
        case 'visit_cancelled':
          toast(
            <CustomToast 
              title={notification.title}
              message={notification.message}
              icon={X}
              color="danger"
            />,
            toastConfig
          );
          break;
          
        case 'new_user':
          toast(
            <CustomToast 
              title={notification.title}
              message={notification.message}
              icon={UserPlus}
              color="success"
            />,
            toastConfig
          );
          break;
          
        default:
          toast(
            <CustomToast 
              title={notification.title}
              message={notification.message}
              icon={AlertCircle}
              color="info"
            />,
            toastConfig
          );
      }
    };

    const socket = socketManager.get();
    
    // Remove any existing listeners first
    socket.off('newNotification', handleNewNotification);
    
    // Add the listener
    socket.on('newNotification', handleNewNotification);

    return () => {
      console.log('NotificationToastListener: Cleaning up listener');
      socket.off('newNotification', handleNewNotification);
    };
  }, [user?.id]); // Use user.id as dependency to ensure proper cleanup

  // This component doesn't render anything
  return null;
}